/**
 * Assignee Dropdown Component
 * ===========================
 * Dropdown for assigning cases to team members.
 * Features:
 * - List of team members with roles
 * - "Assign to me" quick action
 * - Unassign option
 * - Shows current assignee
 */

import React, { useState, useRef, useEffect } from 'react';
import { User, ChevronDown, Check, X, Loader2 } from 'lucide-react';
import { useTeamMembers } from '../../hooks/useSettings';
import { useAssignCase } from '../../hooks/useCases';
import { useAuth } from '../../auth';
import { useToast } from '../../contexts/ToastContext';

export default function AssigneeDropdown({ caseId, currentAssignee, onAssigned }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const toast = useToast();
  const { user } = useAuth();

  const { data: teamData, isLoading: loadingTeam } = useTeamMembers();
  const assignCase = useAssignCase();

  const teamMembers = teamData?.members || teamData || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAssign = async (userId, userName) => {
    try {
      await assignCase.mutateAsync({ id: caseId, userId });
      toast.success(userId ? `Assigned to ${userName}` : 'Case unassigned');
      setIsOpen(false);
      if (onAssigned) onAssigned(userId);
    } catch (err) {
      toast.error('Failed to assign case');
    }
  };

  const currentAssigneeName = currentAssignee?.name || currentAssignee?.email || null;
  const currentUserId = user?.sub || user?.id;

  return (
    <div style={styles.container} ref={dropdownRef}>
      <button
        style={styles.trigger}
        onClick={() => setIsOpen(!isOpen)}
        disabled={assignCase.isPending}
      >
        <div style={styles.triggerContent}>
          <div style={styles.avatar}>
            {currentAssigneeName ? currentAssigneeName[0].toUpperCase() : <User size={14} />}
          </div>
          <span style={styles.label}>
            {currentAssigneeName || 'Unassigned'}
          </span>
        </div>
        {assignCase.isPending ? (
          <Loader2 size={14} style={styles.spinner} />
        ) : (
          <ChevronDown size={14} style={{ transform: isOpen ? 'rotate(180deg)' : 'none' }} />
        )}
      </button>

      {isOpen && (
        <div style={styles.dropdown}>
          {/* Assign to me */}
          {currentUserId && currentAssignee?.id !== currentUserId && (
            <button
              style={styles.option}
              onClick={() => handleAssign(currentUserId, 'Me')}
            >
              <div style={styles.optionContent}>
                <div style={{ ...styles.optionAvatar, background: 'var(--color-primary)' }}>
                  <User size={12} color="white" />
                </div>
                <span style={styles.optionName}>Assign to me</span>
              </div>
            </button>
          )}

          {/* Divider */}
          {currentUserId && currentAssignee?.id !== currentUserId && (
            <div style={styles.divider} />
          )}

          {/* Unassign option */}
          {currentAssignee && (
            <>
              <button
                style={styles.option}
                onClick={() => handleAssign(null, null)}
              >
                <div style={styles.optionContent}>
                  <div style={{ ...styles.optionAvatar, background: 'var(--bg-tertiary)' }}>
                    <X size={12} />
                  </div>
                  <span style={styles.optionName}>Unassign</span>
                </div>
              </button>
              <div style={styles.divider} />
            </>
          )}

          {/* Team members */}
          {loadingTeam ? (
            <div style={styles.loading}>
              <Loader2 size={14} style={styles.spinner} />
              <span>Loading team...</span>
            </div>
          ) : teamMembers.length === 0 ? (
            <div style={styles.empty}>No team members found</div>
          ) : (
            teamMembers.map((member) => {
              const isCurrentAssignee = currentAssignee?.id === member.id ||
                currentAssignee?.user_id === member.user_id;
              const memberId = member.user_id || member.id;
              const memberName = member.name || member.email;

              return (
                <button
                  key={memberId}
                  style={{
                    ...styles.option,
                    ...(isCurrentAssignee ? styles.optionSelected : {}),
                  }}
                  onClick={() => handleAssign(memberId, memberName)}
                  disabled={isCurrentAssignee}
                >
                  <div style={styles.optionContent}>
                    <div style={styles.optionAvatar}>
                      {memberName?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div style={styles.optionInfo}>
                      <span style={styles.optionName}>{memberName}</span>
                      {member.role && (
                        <span style={styles.optionRole}>{member.role}</span>
                      )}
                    </div>
                  </div>
                  {isCurrentAssignee && <Check size={14} style={{ color: 'var(--color-success)' }} />}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    position: 'relative',
  },
  trigger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '8px 12px',
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    cursor: 'pointer',
    width: '100%',
    minWidth: '180px',
    transition: 'all 0.15s',
  },
  triggerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  avatar: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    background: 'var(--bg-tertiary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--text-secondary)',
  },
  label: {
    fontSize: '13px',
    color: 'var(--text-primary)',
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '4px',
    background: 'var(--bg-primary)',
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 100,
    maxHeight: '280px',
    overflowY: 'auto',
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '10px 12px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    transition: 'background 0.15s',
  },
  optionSelected: {
    background: 'var(--bg-secondary)',
  },
  optionContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  optionAvatar: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: 'var(--bg-tertiary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--text-secondary)',
  },
  optionInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  optionName: {
    fontSize: '13px',
    color: 'var(--text-primary)',
  },
  optionRole: {
    fontSize: '11px',
    color: 'var(--text-muted)',
  },
  divider: {
    height: '1px',
    background: 'var(--border-color)',
    margin: '4px 0',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    color: 'var(--text-muted)',
    fontSize: '13px',
  },
  empty: {
    padding: '12px',
    color: 'var(--text-muted)',
    fontSize: '13px',
    textAlign: 'center',
  },
  spinner: {
    animation: 'spin 1s linear infinite',
  },
};
